<!--
@component
NextMatchesWidget

Renders a list of matches as a responsive rail (1 / 2 / 3 cards based
on viewport width). Callers are responsible for handing over the
pre-filtered list (e.g. live + upcoming for the global header, the
team's full schedule for an embed).
-->

<script lang="ts">
  import { onMount } from 'svelte';
  import Match from './Match.svelte';
  import type { ContestedMatch } from '$lib/@types/opta';

  // Breakpoints kept in sync with the SCSS media queries below.
  const BREAKPOINT_WIDE = 720;
  const BREAKPOINT_NARROW = 545;
  const BREAKPOINT_NARROWER = 350;
  const MATCH_WIDTH_PX = 150;
  // const BREAKPOINT_NARROW = 600;
  // const BREAKPOINT_NARROWER = 375;
  // const MATCH_WIDTH_PX = 175;

  type Props = {
    /** Pre-filtered, pre-sorted list of matches to render. */
    matches: ContestedMatch[];
    /**
     * Maximum number of matches to display. When omitted, falls back
     * to a responsive default (1 / 2 / 3 cards).
     */
    maxMatches?: number;
  };

  const { matches, maxMatches }: Props = $props();

  // Responsive default driven by `matchMedia` so JS thresholds stay
  // aligned with the SCSS breakpoints. Initialised synchronously on
  // the client so the first paint already has the right count; SSR
  // defaults to 1 so client hydration grows 1 → N rather than
  // shrinking N → fewer.
  const initialResponsiveCount = () => {
    if (typeof window === 'undefined') return 1;
    if (window.matchMedia(`(max-width: ${BREAKPOINT_NARROWER}px)`).matches)
      return 1;
    if (window.matchMedia(`(max-width: ${BREAKPOINT_NARROW}px)`).matches)
      return 2;
    return 3;
  };

  let responsiveCount = $state(initialResponsiveCount());

  onMount(() => {
    const narrow = window.matchMedia(`(max-width: ${BREAKPOINT_NARROW}px)`);
    const narrower = window.matchMedia(`(max-width: ${BREAKPOINT_NARROWER}px)`);
    const wide = window.matchMedia(`(max-width: ${BREAKPOINT_WIDE}px)`);

    const update = () => {
      if (narrower.matches) responsiveCount = 1;
      else if (narrow.matches) responsiveCount = 2;
      else if (wide.matches) responsiveCount = 3;
      else responsiveCount = 4;
    };

    update();
    narrow.addEventListener('change', update);
    narrower.addEventListener('change', update);
    wide.addEventListener('change', update);

    return () => {
      narrow.removeEventListener('change', update);
      narrower.removeEventListener('change', update);
      wide.addEventListener('change', update);
    };
  });

  const count = $derived(maxMatches ?? responsiveCount);
  const matchList = $derived(matches.slice(0, count));
  const cardsPerRow = $derived(Math.min(matchList.length, 3));

  const keyOf = (m: ContestedMatch, i: number) => m?.matchInfo?.id ?? i;
</script>

{#if matchList.length}
  <div
    class="match-rail-wrapper relative fmy-3 mx-auto flex justify-center"
    class:wrap={matchList.length > 3}
    style="--matchCount:{matchList.length}; --cards-per-row:{cardsPerRow}; --match-width:{MATCH_WIDTH_PX}px;"
  >
    {#each matchList as match, i (keyOf(match, i))}
      <Match {match} />
    {/each}
  </div>
{/if}

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  // Keep in sync with the constants in the <script> block.
  $bp-narrow: 545px;
  $bp-narrower: 350px;

  .match-rail-wrapper {
    @include mixins.hard-shadow;
    background-color: var(--theme-colour-muted-grey);
    border-radius: calc(1 * var(--theme-style-border-radius));
    width: calc(var(--matchCount) * var(--match-width, 175px));
    z-index: 1;

    @media (max-width: $bp-narrow) {
      max-width: mixins.$column-width-narrower;
    }
    @media (max-width: $bp-narrower) {
      max-width: #{mixins.$column-width-narrower * 0.5};
    }

    :global(.live-container) {
      position: absolute !important;
      top: -0.75rem;
      left: 50%;
      transform: translateX(-50%);
    }
  }
</style>
